import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LegalLinks from '../../components/LegalLinks';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Briefcase, MapPin, Clock, ArrowRight, Building, 
  Users, Sparkles, ChevronRight, Search, Filter, X, Menu, Share2,
} from 'lucide-react';

const API_URL = '';

export default function CareersPublicPage() {
  const navigate = useNavigate();
  const { roleId: routeRoleId } = useParams();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    fetchRoles();
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // If the URL carries /careers/role/:roleId, auto-scroll to that role and
  // pre-fill the search so the applicant lands directly on what was shared.
  useEffect(() => {
    if (!routeRoleId || !roles.length) return;
    const role = roles.find((r) => r._id === routeRoleId);
    if (role) {
      // Pre-fill search so the role visually highlights
      setSearchTerm(role.designation || '');
      // Smooth-scroll to it after layout settles
      setTimeout(() => {
        const el = document.querySelector(`[data-role-card="${routeRoleId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [routeRoleId, roles]);

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/career/roles/public`);
      setRoles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = roles.filter(role => {
    const matchesSearch = role.designation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         role.department_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = !selectedDepartment || role.department_name === selectedDepartment;
    return matchesSearch && matchesDept;
  });

  const departments = [...new Set(roles.map(r => r.department_name).filter(Boolean))];

  const handleApply = (roleId) => {
    // Carry the chosen roleId through login → dashboard so the apply modal
    // can auto-open. Persist in localStorage as a backup in case the user
    // closes/reopens the browser mid-flow.
    if (roleId) {
      try { localStorage.setItem('pending_apply_role', roleId); } catch (e) { /* ignore */ }
    }
    const token = localStorage.getItem('career_token');
    if (token) {
      navigate(`/career/dashboard${roleId ? `?role=${encodeURIComponent(roleId)}` : ''}`);
    } else {
      navigate(`/career/login${roleId ? `?role=${encodeURIComponent(roleId)}&next=dashboard` : ''}`);
    }
  };

  // Shareable link → Web Share API on mobile, fallback to clipboard
  const handleShare = async (role, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const url = `${window.location.origin}/careers/role/${role._id}`;
    const title = `We're hiring — ${role.designation} at Girgit`;
    const text = `${role.designation}${role.department_name ? ` · ${role.department_name}` : ''} — Apply now at Girgit.`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied — paste it anywhere');
      }
    } catch (err) {
      // Either user cancelled (AbortError) or clipboard denied — try one more fallback
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied — paste it anywhere');
      } catch (e2) {
        toast.error('Could not copy the link');
      }
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDepartment('');
    setShowMobileFilters(false);
  };

  // ============================================
  // MOBILE VIEW
  // ============================================
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50" data-testid="careers-public-mobile">
        {/* Mobile Header */}
        <header className="bg-white/95 backdrop-blur-sm border-b border-white/40 sticky top-0 z-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-6 h-6" />
              <span className="font-bold text-[#1a1a2e]">Girgit</span>
              <span className="text-xs bg-indigo-500 text-white px-2 py-0.5 rounded-full">Careers</span>
            </Link>
            <Link 
              to="/career/login"
              className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl text-sm font-medium"
            >
              Apply
            </Link>
          </div>
        </header>

        {/* Mobile Hero - Compact */}
        <section className="px-4 py-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-8 h-8" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-8 h-8" />
          </div>
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded-full text-xs font-medium mb-4">
            <Sparkles className="w-3 h-3" />
            We&apos;re Hiring!
          </div>
          <h1 className="text-2xl font-bold text-[#1a1a2e] mb-3">
            Join Our Team
          </h1>
          <p className="text-sm text-neutral-600 mb-4">
            Be part of an innovative marketing agency shaping the future.
          </p>
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-soft border border-white/40">
            <span className="text-2xl font-bold text-indigo-600">{roles.length}</span>
            <span className="text-sm text-neutral-600">Open Positions</span>
          </div>
        </section>

        {/* Mobile Search Bar */}
        <div className="px-4 pb-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search positions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-white/40 rounded-xl text-sm focus:ring-2 focus:ring-indigo-400/30"
              />
            </div>
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className={`px-3 py-2.5 border rounded-xl flex items-center gap-1 ${
                selectedDepartment ? 'border-indigo-500 bg-indigo-50 text-indigo-600' : 'border-white/40 text-neutral-600'
              }`}
            >
              <Filter className="w-4 h-4" />
              {selectedDepartment && <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>}
            </button>
          </div>

          {/* Mobile Filters Dropdown */}
          {showMobileFilters && (
            <div className="mt-3 p-4 glass-card rounded-xl shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-[#1a1a2e]">Filter by Department</h3>
                {selectedDepartment && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-indigo-600 font-medium"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setSelectedDepartment(''); setShowMobileFilters(false); }}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    !selectedDepartment ? 'bg-indigo-500 text-white' : 'bg-neutral-100 text-neutral-700'
                  }`}
                >
                  All
                </button>
                {departments.map(dept => (
                  <button
                    key={dept}
                    onClick={() => { setSelectedDepartment(dept); setShowMobileFilters(false); }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedDepartment === dept ? 'bg-indigo-500 text-white' : 'bg-neutral-100 text-neutral-700'
                    }`}
                  >
                    {dept}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Mobile Job Cards */}
        <section className="px-4 pb-8">
          <h2 className="text-lg font-bold text-[#1a1a2e] mb-4">
            {filteredRoles.length} Position{filteredRoles.length !== 1 ? 's' : ''}
          </h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
              <p className="mt-3 text-sm text-neutral-600">Loading...</p>
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="text-center py-12 glass-card">
              <Briefcase className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <h3 className="font-semibold text-neutral-700 mb-1">No positions found</h3>
              <p className="text-sm text-neutral-500">Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRoles.map((role) => (
                <div 
                  key={role._id}
                  className={`glass-card p-4 active:scale-[0.99] transition-transform ${routeRoleId === role._id ? 'ring-2 ring-indigo-400 ring-offset-2' : ''}`}
                  data-testid={`mobile-role-card-${role._id}`}
                  data-role-card={role._id}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#1a1a2e] text-base">
                        {role.designation}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-neutral-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Building className="w-3 h-3" />
                          {role.department_name || 'General'}
                        </span>
                        <span>•</span>
                        <span>Full-time</span>
                      </div>
                    </div>
                  </div>
                  
                  {role.qualifications && (
                    <p className="text-xs text-neutral-600 mb-3 line-clamp-2">{role.qualifications}</p>
                  )}

                  {role.skills && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {role.skills.split(',').slice(0, 3).map((skill, i) => (
                        <span 
                          key={i}
                          className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs"
                        >
                          {skill.trim()}
                        </span>
                      ))}
                      {role.skills.split(',').length > 3 && (
                        <span className="px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded-full text-xs">
                          +{role.skills.split(',').length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleApply(role._id)}
                      data-testid={`apply-now-mobile-${role._id}`}
                      className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                    >
                      Apply Now
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => handleShare(role, e)}
                      data-testid={`share-role-mobile-${role._id}`}
                      aria-label="Share role"
                      className="w-11 h-11 flex items-center justify-center rounded-xl border border-neutral-200 bg-white active:scale-[0.95] transition-transform"
                    >
                      <Share2 className="w-4 h-4 text-neutral-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Mobile CTA */}
        <section className="px-4 py-8 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="text-center">
            <h2 className="text-xl font-bold text-white mb-2">
              Ready to Start?
            </h2>
            <p className="text-indigo-100 text-sm mb-4">
              Create an account to apply and track applications.
            </p>
            <Link 
              to="/career/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 rounded-xl font-semibold text-sm"
            >
              Get Started
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Mobile Footer */}
        <footer className="bg-[#111111] py-8 px-4">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center gap-2 mb-4">
              <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-5 h-5" />
              <img src="/girgit-logo.png" alt="Girgit" className="w-5 h-5" />
              <span className="text-lg font-semibold text-white">Girgit</span>
            </div>
            <p className="text-xs text-neutral-600">
              © {new Date().getFullYear()} Half Eaten Donut
            </p>
          </div>
        </footer>
      </div>
    );
  }

  // ============================================
  // DESKTOP VIEW (Original - Unchanged)
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50" data-testid="careers-public-desktop">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-white/40 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-6 h-6" />
            <img src="/hed-logo-black.png" alt="Half Eaten Donut" className="h-7" />
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            <img src="/girgit-logo.png" alt="Girgit" className="w-6 h-6" />
            <span className="font-bold text-lg text-[#1a1a2e]">Girgit</span>
            <span className="text-xs bg-indigo-500 text-white px-2 py-0.5 rounded-full">Careers</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link 
              to="/career/login"
              className="px-4 py-2 text-neutral-700 hover:text-[#1a1a2e] font-medium transition-colors"
            >
              Sign In
            </Link>
            <Link 
              to="/career/login"
              className="px-5 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-medium hover:opacity-90 transition-opacity shadow-soft"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center max-w-4xl">
          {/* HED | Girgit Branding */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-12 h-12" />
            <img src="/hed-logo-black.png" alt="Half Eaten Donut" className="h-14" />
            <div className="w-px h-12 bg-gray-300 mx-2"></div>
            <img src="/girgit-logo.png" alt="Girgit" className="w-12 h-12" />
            <span className="text-4xl font-bold text-[#1a1a2e]">Girgit</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            We&apos;re Hiring!
          </div>
          <h1 className="text-5xl font-bold text-[#1a1a2e] mb-6">
            Join Our Growing Team
          </h1>
          <p className="text-xl text-neutral-600 mb-10">
            Be part of an innovative marketing agency that&apos;s shaping the future of digital experiences. 
            We&apos;re looking for talented individuals who are passionate about creativity and excellence.
          </p>
          
          {/* Stats - Only Open Positions */}
          <div className="flex items-center justify-center mb-10">
            <div className="text-center">
              <div className="text-4xl font-bold text-indigo-600">{roles.length}</div>
              <div className="text-neutral-600">Open Positions</div>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="py-8 px-4 bg-white border-y border-white/40">
        <div className="container mx-auto max-w-4xl">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search positions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="pl-12 pr-8 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-500 appearance-none bg-white min-w-[200px]"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            {/* Clear Filters Button */}
            {(searchTerm || selectedDepartment) && (
              <button
                onClick={clearFilters}
                className="px-4 py-3 text-sm font-medium text-neutral-600 hover:text-[#1a1a2e] border border-white/40 rounded-xl hover:bg-white/60 transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-2xl font-bold text-[#1a1a2e] mb-8">
            {filteredRoles.length} Open Position{filteredRoles.length !== 1 ? 's' : ''}
          </h2>

          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-neutral-600">Loading positions...</p>
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="text-center py-20 glass-card">
              <Briefcase className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-neutral-700 mb-2">No positions found</h3>
              <p className="text-neutral-500">Check back soon for new opportunities!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRoles.map((role) => (
                <div 
                  key={role._id}
                  className={`glass-card p-6 hover:border-indigo-300 hover:shadow-lg transition-all duration-300 group ${routeRoleId === role._id ? 'ring-2 ring-indigo-400 ring-offset-2' : ''}`}
                  data-testid={`desktop-role-card-${role._id}`}
                  data-role-card={role._id}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                          <Briefcase className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-[#1a1a2e] group-hover:text-indigo-600 transition-colors">
                            {role.designation}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-neutral-500">
                            <span className="flex items-center gap-1">
                              <Building className="w-4 h-4" />
                              {role.department_name || 'General'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              Full-time
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {role.qualifications && (
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-neutral-700 mb-2">Requirements:</h4>
                          <p className="text-sm text-neutral-600 line-clamp-2">{role.qualifications}</p>
                        </div>
                      )}

                      {role.skills && (
                        <div className="flex flex-wrap gap-2">
                          {role.skills.split(',').slice(0, 4).map((skill, i) => (
                            <span 
                              key={i}
                              className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium"
                            >
                              {skill.trim()}
                            </span>
                          ))}
                          {role.skills.split(',').length > 4 && (
                            <span className="px-3 py-1 bg-neutral-100 text-neutral-600 rounded-full text-xs font-medium">
                              +{role.skills.split(',').length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={() => handleApply(role._id)}
                        data-testid={`apply-now-desktop-${role._id}`}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-medium hover:opacity-90 transition-opacity shadow-soft group-hover:scale-105 hover:scale-105"
                      >
                        Apply
                        <ArrowRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => handleShare(role, e)}
                        data-testid={`share-role-desktop-${role._id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-neutral-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <Share2 className="w-3.5 h-3.5" />
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-indigo-100 mb-8 text-lg">
            Create an account to apply for positions and track your applications.
          </p>
          <Link 
            to="/career/login"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-indigo-50 transition-colors shadow-lg"
          >
            Get Started
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111111] py-10">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center">
            {/* HED | Girgit Branding */}
            <div className="flex items-center gap-3 mb-6">
              <img src="/hed-icon.png" alt="Half Eaten Donut" className="w-6 h-6" />
              <img src="/hed-logo-white.png" alt="Half Eaten Donut" className="h-8" />
              <div className="w-px h-6 bg-charcoal-600 mx-2"></div>
              <img src="/girgit-logo.png" alt="Girgit" className="w-6 h-6" />
              <span className="text-xl font-semibold text-white">Girgit</span>
            </div>
            
            <p className="text-sm text-neutral-500 mb-2">
              Join our team and shape the future of creative marketing
            </p>
            <LegalLinks tone="dark" className="mb-2" />
            <p className="text-xs text-neutral-600">
              © {new Date().getFullYear()} Half Eaten Donut. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
